import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { ScreenContext, useApp, useAuth, usePopUpMessage } from '@hb/react'
import {
  colors,
  MessageThread,
  messageThreadCollections,
  patientColor,
  ThreadType,
} from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useDocument } from '../../backend/db/hooks/useDocument'
import chatIcon from '../../icons/chat.svg'
import { PulsingCircle } from '../Animation/PulsingCirlce'
import './styles.css'
import { ThreadView } from './ThreadView/ThreadView'
import { reportThreadRead } from './utils'

export const ThreadPopover = ({
  threadType,
  id,
  unreadThreadMessages,
  mobileView,
}: {
  threadType: ThreadType
  id: string
  unreadThreadMessages: number
  mobileView?: boolean
}) => {
  const me = useAuth(s => s.authUser)
  const { data: thread } = useDocument<MessageThread>(messageThreadCollections[threadType], id)

  const [hasOpened, setHasOpened] = useState(false)
  const { appName } = useApp()

  const buttonBg = useMemo(
    () => (threadType === ThreadType.PATIENT ? patientColor : colors.green.hex),
    [threadType],
  )

  const [markingRead, setMarkingRead] = useState(false)
  const { showError } = usePopUpMessage()

  const handleOpen = useCallback(() => {
    setHasOpened(true)
    const uid = me?.uid ?? null
    if (!uid) return
    if (threadType !== ThreadType.PATIENT && threadType !== ThreadType.ASSESSMENT) return
    const shouldMarkRead =
      !!(unreadThreadMessages || thread?.unreadBy.includes(me?.uid ?? '')) && !markingRead
    if (shouldMarkRead) {
      setMarkingRead(true)
      reportThreadRead(appName, threadType, id)
        .catch(e => showError(e.message))
        .finally(() => setMarkingRead(false))
    }
  }, [threadType, unreadThreadMessages, thread, me, appName, id, showError, markingRead])

  const { width: screenWidth } = useContext(ScreenContext)
  const width = useMemo(() => Math.min(screenWidth, 450), [screenWidth])
  const unread = useMemo(
    () => !!unreadThreadMessages || !!(me && thread?.unreadBy?.includes(me.uid)),
    [unreadThreadMessages, me, thread],
  )
  const { isMobile } = useContext(ScreenContext)
  const tooltipLabel = useMemo(() => {
    if (threadType === ThreadType.PATIENT) return 'Patient Chat'
    return appName === 'app' ? 'Provider Chat' : 'HB Chat'
  }, [threadType, appName])
  const unreadClassName = useMemo(() => {
    if (threadType === ThreadType.PATIENT) return 'pulsating-shadow-white'
    return 'pulsating-shadow-green'
  }, [threadType])
  const triggerText = useMemo(() => {
    if (threadType === ThreadType.PATIENT) return 'PATIENT'
    return appName === 'app' ? 'PROVIDER' : 'HB CHAT'
  }, [threadType, appName])
  return (
    <HStack maxW="350px" justify="flex-end">
      <Popover
        closeOnBlur={false}
        strategy="fixed"
        isLazy={!hasOpened}
        onOpen={handleOpen}
        placement={isMobile ? 'bottom-end' : 'left-start'}>
        {({ onClose, isOpen }) => (
          <>
            <PopoverTrigger>
              <Box position="relative">
                <Tooltip
                  bg={threadType === ThreadType.PATIENT ? patientColor : colors.green.hex}
                  placement="top"
                  hasArrow
                  fontSize="sm"
                  textShadow="1px 1px 3px #00000099"
                  label={tooltipLabel}>
                  <Button
                    bg={buttonBg}
                    className={unread ? unreadClassName : 'not-pulsating-shadow'}
                    size="sm"
                    borderRadius={6}
                    h="auto"
                    isLoading={markingRead}
                    aria-label="Thread">
                    <Flex gap={1} align="center" py={1}>
                      {mobileView ? null : (
                        <Text
                          textShadow="1px 1px 3px #00000077"
                          fontWeight={500}
                          fontSize="xs"
                          fontFamily="Hero-New"
                          color="white">
                          {triggerText}
                        </Text>
                      )}
                      <Flex gap={1} px={unread ? 2 : 0} align="center">
                        <Image
                          transform="scale(-1,1)"
                          width="22px"
                          filter="invert(1) drop-shadow(1px 1px 3px rgba(0,0,0,0.4))"
                          src={chatIcon}
                          alt="Thread"
                        />
                      </Flex>
                    </Flex>
                    {unread ? (
                      <PulsingCircle
                        style={{
                          position: 'absolute',
                          height: '14px',
                          width: '14px',
                          top: '4px',
                          right: '-4px',
                        }}
                      />
                    ) : null}
                  </Button>
                </Tooltip>
              </Box>
            </PopoverTrigger>
            <PopoverContent border="none" width={`${width}px`}>
              <ThreadView
                uid={me?.uid ?? null}
                width={width}
                isOpen={isOpen}
                threadType={threadType}
                assessmentThreadUnread={unread}
                threadId={id}
                inPopover
                onBack={onClose}
              />
            </PopoverContent>
          </>
        )}
      </Popover>
    </HStack>
  )
}
